import { IssueCandidate } from 'api/api.types';
import getCandidatesIncludingEmptySeats from 'utils/getCandidatesIncludingEmptySeats';

export interface FormattedList {
  title: string;
  shortTitle: string;
  seatsClaimed: number;
  listId: number;
  partyId?: number;
  listNumber: string;
  votesRelative: number;
  votesAbsolute: number;
  color?: string;
}

export const getFormattedCandidates = (
  candidates: IssueCandidate[],
  lists: FormattedList[],
  formattedParties: FormattedList[],
) => {
  if (!candidates || !lists) {
    return undefined;
  }

  // In rare cases, a list could win more seats than candidates, so we need to add placeholders for those seats.
  // For these candidates, all we know is the list/party
  const listsWithEmptySeats = lists
    .map((list) => {
      const { seatsClaimed, listNumber, listId } = list;
      if (seatsClaimed === undefined) {
        return {};
      }
      const electedListCandidates = candidates
        .filter((candidate) => candidate.results.elected)
        .filter(
          (candidate) =>
            candidate.list_id === list.listId || candidate.list_nr === list.listNumber,
        );

      const emptySeats = seatsClaimed - electedListCandidates.length;

      return {
        listId,
        listNr: listNumber,
        emptySeats,
      };
    })
    .filter((emptySeat) => emptySeat?.emptySeats > 0);

  // caused by the cumulation-method of candidates in our archive, we have duplicates in the list of candidates
  const uniqueCandidatesMap = {};
  let uniqueCandidates = candidates.filter(({ list_nr, id }) => {
    const key = `${list_nr}_${id}`;
    if (uniqueCandidatesMap[key]) {
      return false;
    }
    uniqueCandidatesMap[key] = true;
    return true;
  });

  uniqueCandidates = getCandidatesIncludingEmptySeats(
    uniqueCandidates,
    listsWithEmptySeats,
  );

  const formattedCandidates = uniqueCandidates.map((candidate) => {
    const listInfo = lists?.find(
      (p) => p.listId === candidate.list_id || p.listNumber === candidate.list_nr,
    );

    const party = formattedParties?.find((p) => p.listId === listInfo.partyId);

    return {
      id: candidate.id,
      candidateNumber: candidate.number,
      firstName: candidate.first_name,
      lastName: candidate.last_name,
      isUpToNow: candidate.is_up_to_now,
      inOfficeSince: candidate.in_office_since_year,
      party: party?.shortTitle || listInfo.shortTitle, // if we cannot find a party, we use the list title as last ressort
      location: candidate.city,
      votes: candidate.results?.votes,
      list: listInfo?.listNumber,
      listName: listInfo.shortTitle,
      gender: candidate.gender,
      elected: candidate.results?.elected,
      photoPath: candidate.photo_path,
      isArchiveCandidate: candidate.is_archive_candidate,
    };
  });

  return formattedCandidates;
};
